import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const LanguageSelector = ({ variant = 'default', dropdownPosition = 'bottom' }) => {
  const { currentLang, setLanguage, languages, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages[currentLang];

  const handleSelect = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  // Get all languages as array to ensure consistent order
  const languageList = [
    languages.en,
    languages.tr,
    languages.el,
    languages.ar
  ].filter(Boolean);

  if (variant === 'minimal') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-2 py-1.5 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
        >
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">{currentLanguage?.code?.toUpperCase()}</span>
        </button>

        <AnimatePresence>
          {isOpen && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setIsOpen(false)} 
              />
              <motion.div
                initial={{ opacity: 0, y: dropdownPosition === 'top' ? 10 : -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: dropdownPosition === 'top' ? 10 : -10 }}
                className={`absolute right-0 w-44 bg-[#12121a] border border-[#1e1e2e] rounded-xl shadow-xl z-50 overflow-hidden ${
                  dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                }`}
              >
                {languageList.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleSelect(lang.code)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${
                      currentLang === lang.code
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    <span>{lang.nativeName}</span>
                    {currentLang === lang.code && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Default variant
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-[#1e1e2e] hover:bg-[#2a2a3a] text-gray-300 rounded-xl transition-colors"
      >
        <Globe className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-sm">{currentLanguage?.nativeName}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)} 
            />
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute right-0 top-full mt-2 w-48 bg-[#12121a] border border-[#1e1e2e] rounded-xl shadow-xl z-50 overflow-hidden"
            >
              <div className="p-2">
                <p className="px-2 py-1.5 text-xs text-gray-500 font-medium uppercase tracking-wide">
                  {t('selectLanguage')}
                </p>
              </div>
              <div className="border-t border-[#1e1e2e]">
                {languageList.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleSelect(lang.code)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors ${
                      currentLang === lang.code
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{lang.nativeName}</span>
                      <span className="text-xs text-gray-500">{lang.name}</span>
                    </div>
                    {currentLang === lang.code && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
